import React, { useEffect, useState, useCallback } from "react";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ShadowBox from "../../layoutComponents/shadowBox/ShadowBox";
import CommitsLog from "../../components/commitsLog/CommitsLog";
import PortfolioDisplay from "../../components/portfolioDisplay/PortfolioDisplay";
import PortfolioProjectForm from "../../components/portfolioProjectForm/PortfolioProjectForm";

import "./StudentDetailSubviews.scss";
import "./Portfolio.scss";

function Portfolio({ playerInfo, isProfile }) {
  const [portfolioData, setPortfolioData] = React.useState(null);
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [successfulUpdate, setSuccessfulUpdate] = useState(false);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  const errorElement = <Alert severity='error'>{snackbarMessage}</Alert>;
  const successElement = <Alert>Portfolio was updated successfully!</Alert>;

  const fetchTodos = useCallback(async () => {
    if (!isProfile) return;

    const res = await fetch(`${process.env.REACT_APP_API}/todos`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });
    if (res.ok) {
      const json = await res.json();
      setTodos(json);
    }
  }, [isProfile]);

  useEffect(() => {
    if (playerInfo.uuid) {
      setLoading(true);
      const api = `${process.env.REACT_APP_API}/portfolios/${playerInfo.uuid}`;
      fetch(api)
        .then((res) => res.json())
        .then((result) => {
          setPortfolioData(result);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching portfolio:", err);
          setPortfolioData(null);
          setLoading(false);
        });
    }
  }, [playerInfo, successfulUpdate]);

  // Check if portfolio has data
  const hasPortfolioData = portfolioData && Object.values(portfolioData).length > 0;

  // Check if viewing own profile (either on /profile route OR matching UUID on /fellow route)
  const isOwnProfile = isProfile;

  // Render empty state for own profile
  const renderOwnProfileEmptyState = () => (
    <ShadowBox>
      <div 
        className="portfolio__emptyState portfolio__emptyState--clickable"
        onClick={() => setShowEditForm(true)}
      >
        <div className="portfolio__emptyState__icon">📁</div>
        <h3 className="portfolio__emptyState__title">Start Your Portfolio</h3>
        <p className="portfolio__emptyState__description">
          Showcase your best work! Click here to add your first portfolio project.
        </p>
        <button className="portfolio__emptyState__button">
          Add Portfolio Project
        </button>
      </div>
    </ShadowBox>
  );

  // Render empty state for other user's profile
  const renderOtherUserEmptyState = () => (
    <ShadowBox>
      <div className="portfolio__emptyState">
        <div className="portfolio__emptyState__icon">🔒</div>
        <h3 className="portfolio__emptyState__title">No Portfolio Yet</h3>
        <p className="portfolio__emptyState__description">
          {playerInfo.first_name} {playerInfo.last_name} has not yet added a portfolio project. Check back later!
        </p>
      </div>
    </ShadowBox>
  );

  if (loading) {
    return (
      <div className="portfolio">
        <ShadowBox>
          <p>Loading portfolio...</p>
        </ShadowBox>
      </div>
    );
  }

  return (
    <div className="portfolio">
      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
      >
        {successfulUpdate ? successElement : errorElement}
      </Snackbar>

      {/* Show portfolio if data exists and form is not open */}
      {hasPortfolioData && !showEditForm && (
        <ShadowBox>
          <PortfolioDisplay
            portfolioData={portfolioData}
            setShowEditForm={setShowEditForm}
            showEditForm={showEditForm}
            setSuccessfulUpdate={setSuccessfulUpdate}
            isOwnProfile={isProfile}
          />
        </ShadowBox>
      )}

      {/* Show empty state if no portfolio data and form is not open */}
      {!hasPortfolioData && !showEditForm && (
        isProfile ? renderOwnProfileEmptyState() : renderOtherUserEmptyState()
      )}

      {/* Show form if user wants to edit/add portfolio */}
      {playerInfo?.uuid && showEditForm && (
        <PortfolioProjectForm
          userId={playerInfo.uuid}
          setShowEditForm={setShowEditForm}
          setShowSnackbar={setShowSnackbar}
          snackbarMessage={snackbarMessage}
          setSnackbarMessage={setSnackbarMessage}
          setSuccessfulUpdate={setSuccessfulUpdate}
        />
      )}

      {/* Only show commits log on own profile */}
      {isProfile && (
        <ShadowBox title="Commit Tracker">
          <CommitsLog playerInfo={playerInfo} fetchTodos={fetchTodos} />
        </ShadowBox>
      )}
    </div>
  );
}

export default Portfolio;